import { getSupabase } from '@/lib/supabase';
import { getStaticBlogPostBySlug, getStaticBlogPosts } from '@/lib/blogStaticAdapter';
import { getBlogCanonicalSlug } from '@/lib/blogSlugs';
import { normalizeImageList } from '@/lib/projectMedia';

const adminContentApiBaseUrl = import.meta.env.VITE_CONTENT_API_BASE_URL?.replace(/\/$/, '');

export type LocalizedText = {
  tr: string;
  en: string;
};

function mergeLocalizedText(
  primary: LocalizedText | null | undefined,
  fallback: LocalizedText | null | undefined,
): LocalizedText | undefined {
  if (!fallback && !primary) return undefined;
  return {
    tr: primary?.tr?.trim() || fallback?.tr?.trim() || '',
    en: primary?.en?.trim() || fallback?.en?.trim() || '',
  };
}

const STATIC_TABLE_SECTION_HEADINGS: LocalizedText[] = [
  {
    tr: '## Örnek TRY bantları (abonelikli B2B SaaS profili)',
    en: '## Illustrative TRY bands (subscription B2B SaaS profile)',
  },
  {
    tr: '## Örnek TRY bantları (mobil ürün profili)',
    en: '## Illustrative TRY bands (mobile product profile)',
  },
];

/** When CMS body omits repo-only HTML (cost tables), append the static catalog block. */
export function mergeBlogContentI18n(
  primary: LocalizedText | null | undefined,
  fallback: LocalizedText | null | undefined,
): LocalizedText | undefined {
  const base = mergeLocalizedText(primary, fallback);
  if (!fallback?.tr?.trim() && !fallback?.en?.trim()) return base;

  return {
    tr: appendStaticTableSectionIfMissing(base?.tr ?? '', fallback.tr, 'tr'),
    en: appendStaticTableSectionIfMissing(base?.en ?? '', fallback.en, 'en'),
  };
}

function appendStaticTableSectionIfMissing(
  merged: string,
  staticBody: string,
  locale: 'tr' | 'en',
): string {
  const staticTrim = staticBody?.trim() ?? '';
  if (!staticTrim.includes('<table')) return merged;

  const mergedTrim = merged.trim();
  if (mergedTrim.includes('<table')) return mergedTrim;

  const heading = STATIC_TABLE_SECTION_HEADINGS.map((entry) => entry[locale]).find((candidate) =>
    staticTrim.includes(candidate),
  );
  if (!heading) return mergedTrim || staticTrim;
  const start = staticTrim.indexOf(heading);

  const fromHeading = staticTrim.slice(start);
  const nextSection = fromHeading.indexOf('\n## ', heading.length);
  const tableBlock = (nextSection === -1 ? fromHeading : fromHeading.slice(0, nextSection)).trim();

  if (!mergedTrim) return staticTrim;
  if (mergedTrim.includes(heading)) return mergedTrim;
  return `${mergedTrim}\n\n${tableBlock}`;
}

/** CMS rows can override static posts but often ship EN-only i18n — keep TR fallbacks from catalog. */
function mergeBlogPostWithStaticFallback(
  remote: WebsiteBlogPost,
  staticFallback?: WebsiteBlogPost | null,
): WebsiteBlogPost {
  if (!staticFallback) return remote;

  const title_i18n = mergeLocalizedText(remote.title_i18n, staticFallback.title_i18n);
  const excerpt_i18n = mergeLocalizedText(remote.excerpt_i18n, staticFallback.excerpt_i18n);
  const content_i18n = mergeBlogContentI18n(remote.content_i18n, staticFallback.content_i18n);
  const seo_title_i18n = mergeLocalizedText(remote.seo_title_i18n, staticFallback.seo_title_i18n);
  const seo_description_i18n = mergeLocalizedText(
    remote.seo_description_i18n,
    staticFallback.seo_description_i18n,
  );
  const category_i18n = mergeLocalizedText(remote.category_i18n, staticFallback.category_i18n);
  const reading_time_i18n = mergeLocalizedText(remote.reading_time_i18n, staticFallback.reading_time_i18n);
  const intro_i18n = mergeLocalizedText(remote.intro_i18n, staticFallback.intro_i18n);

  return {
    ...remote,
    title_i18n,
    excerpt_i18n,
    intro_i18n,
    content_i18n,
    seo_title_i18n,
    seo_description_i18n,
    category_i18n,
    reading_time_i18n,
    title: title_i18n?.tr?.trim() || remote.title || staticFallback.title,
    excerpt: excerpt_i18n?.tr?.trim() || remote.excerpt || staticFallback.excerpt,
    seo_title: seo_title_i18n?.tr?.trim() || remote.seo_title || staticFallback.seo_title,
    seo_description:
      seo_description_i18n?.tr?.trim() || remote.seo_description || staticFallback.seo_description,
  };
}

export function getLocalizedText(value: LocalizedText | string | null | undefined, locale: 'tr' | 'en', fallback = '') {
  if (value == null) return fallback;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return fallback;
    if (locale === 'tr' && fallback.trim() !== '') return fallback;
    return value;
  }
  const primary = value[locale];
  if (typeof primary === 'string' && primary.trim() !== '') return primary;
  if (fallback.trim() !== '') return fallback;
  const other = locale === 'tr' ? value.en : value.tr;
  if (typeof other === 'string' && other.trim() !== '') return other;
  return '';
}

export type WebsiteProject = {
  id: string;
  name: string;
  name_i18n?: LocalizedText;
  slug: string;
  short_description: string;
  short_description_i18n?: LocalizedText;
  description: string;
  description_i18n?: LocalizedText;
  cover_image: string | null;
  gallery: string[];
  technologies: string[];
  category: string;
  category_i18n?: LocalizedText;
  metrics_i18n?: {
    tr: string[];
    en: string[];
  };
  project_url: string | null;
  github_url: string | null;
  case_study_url: string | null;
  demo_url: string | null;
  order: number;
  featured: boolean;
  published_at: string | null;
};

const projectFallbackMedia: Record<string, { cover: string; gallery: string[] }> = {
  "3d-yaninda": {
    cover: "https://fmsprcpiwfeggvqzvwtv.supabase.co/storage/v1/object/public/content-media/projects/project-1.jpg",
    gallery: [
      "https://fmsprcpiwfeggvqzvwtv.supabase.co/storage/v1/object/public/content-media/projects/project-1.jpg",
    ],
  },
  "ailem-aselsan": {
    cover: "https://fmsprcpiwfeggvqzvwtv.supabase.co/storage/v1/object/public/content-media/projects/project-2.jpg",
    gallery: [
      "https://fmsprcpiwfeggvqzvwtv.supabase.co/storage/v1/object/public/content-media/projects/project-2.jpg",
    ],
  },
  kolektif: {
    cover: "https://fmsprcpiwfeggvqzvwtv.supabase.co/storage/v1/object/public/content-media/projects/project-3.jpg",
    gallery: [
      "https://fmsprcpiwfeggvqzvwtv.supabase.co/storage/v1/object/public/content-media/projects/project-3.jpg",
    ],
  },
  lcw: {
    cover: "https://fmsprcpiwfeggvqzvwtv.supabase.co/storage/v1/object/public/content-media/projects/project-4.jpg",
    gallery: [
      "https://fmsprcpiwfeggvqzvwtv.supabase.co/storage/v1/object/public/content-media/projects/project-4.jpg",
    ],
  },
  ztech: {
    cover: "https://fmsprcpiwfeggvqzvwtv.supabase.co/storage/v1/object/public/content-media/projects/project-1.jpg",
    gallery: [
      "https://fmsprcpiwfeggvqzvwtv.supabase.co/storage/v1/object/public/content-media/projects/project-1.jpg",
    ],
  },
};

function getProjectFallbackMedia(slug: string) {
  return projectFallbackMedia[slug] ?? null;
}

export type WebsiteBlogPost = {
  id: string;
  title: string;
  title_i18n?: LocalizedText;
  slug: string;
  excerpt: string;
  excerpt_i18n?: LocalizedText;
  /** Short direct-answer copy for AEO (may differ from meta excerpt). */
  intro_i18n?: LocalizedText;
  content: string;
  content_i18n?: LocalizedText;
  cover_image: string | null;
  category: string;
  category_i18n?: LocalizedText;
  tags: string[];
  author_name: string;
  seo_title: string | null;
  seo_title_i18n?: LocalizedText;
  seo_description: string | null;
  seo_description_i18n?: LocalizedText;
  reading_time_i18n?: LocalizedText;
  og_image: string | null;
  published_at: string | null;
  created_at: string;
};

export type WebsiteSettings = {
  siteTitle: string;
  defaultMetaDescription: string;
  logoUrl: string;
  faviconUrl: string;
  ctaPrimaryText: string;
  ctaPrimaryUrl: string;
  ctaSecondaryText: string;
  ctaSecondaryUrl: string;
  navbarItems: Array<{ label: string; href: string }>;
  footerGroups: Array<{ title: string; links: Array<{ label: string; href: string }> }>;
  socialLinks: Array<{ platform: string; url: string }>;
  seoDefaults: {
    titleTemplate: string;
    defaultOgImage: string;
  };
};

export type WebsiteAbout = {
  heroTitle: string;
  companySummary: string;
  vision: string;
  mission: string;
  values: string[];
  stats: Array<{ label: string; value: string }>;
  teamHighlights: Array<{ name: string; role: string; bio: string }>;
};

export type WebsiteContact = {
  title: string;
  description: string;
  email: string;
  phone: string;
  address: string;
  workingHours: string;
  mapUrl: string;
  socialLinks: Array<{ platform: string; url: string }>;
  formTargetEmail: string;
  formEnabled: boolean;
};

export type WebsiteHome = {
  heroTitle: string;
  heroSubtitle: string;
  services: Array<{ title: string; description: string }>;
  references: string[];
  process: {
    tag: string;
    title: string;
    docs: string;
    steps: Array<{ title: string; description: string }>;
  };
  testimonials: {
    tag: string;
    title: string;
    items: Array<{ quote: string; name: string; role: string }>;
  };
  techStack: {
    tag: string;
    title: string;
    moreText: string;
    items: string[];
  };
  aboutSection: {
    tag: string;
    title: string;
    description: string;
    stats: Array<{ number: string; label: string }>;
    values: Array<{ title: string; description: string }>;
  };
};

const defaultHomeContent: WebsiteHome = {
  heroTitle: 'Digital Products, Built Right',
  heroSubtitle: 'Design, engineering and delivery in one focused product team.',
  services: [
    { title: 'Product Design', description: 'User-first interface and journey design.' },
    { title: 'Web Engineering', description: 'Modern, high-performance web development.' },
  ],
  references: [],
  process: {
    tag: 'Process',
    title: 'How We Build',
    docs: 'Each project step is documented and shared transparently.',
    steps: [
      { title: 'Discovery', description: 'We align goals, constraints and product scope.' },
      { title: 'Strategy', description: 'We define roadmap, architecture and priorities.' },
      { title: 'Development', description: 'We ship in iterative milestones with quality gates.' },
      { title: 'Launch', description: 'We deploy, monitor and optimize outcomes continuously.' },
    ],
  },
  testimonials: {
    tag: 'Testimonials',
    title: 'What Clients Say',
    items: [
      {
        quote: 'We needed a single site that clearly tells our 3D scanning and digital production story.',
        name: '3D Yanında',
        role: 'Digital production · Turkey',
      },
      {
        quote: 'A conversion-focused homepage for our EdTech brand — AI-forward and kid-friendly.',
        name: 'Ztech',
        role: 'EdTech · AI literacy for kids',
      },
      {
        quote: 'A corporate web presence that presents our exhibition portfolio with B2B credibility.',
        name: 'Kolektif Fuarcılık',
        role: 'International exhibition partner',
      },
    ],
  },
  techStack: {
    tag: 'Tech',
    title: 'Our Stack',
    moreText: 'And more tools tailored to your product context.',
    items: ['React', 'Next.js', 'TypeScript', 'Node.js', 'Supabase', 'AWS'],
  },
  aboutSection: {
    tag: 'About',
    title: 'Built by Product Thinkers',
    description: 'We combine strategy, design and engineering in one execution flow.',
    stats: [],
    values: [
      { title: 'Clarity', description: 'Clear decisions and transparent communication.' },
      { title: 'Ownership', description: 'End-to-end accountability from idea to launch.' },
      { title: 'Craft', description: 'Polished UX and robust engineering quality.' },
    ],
  },
};

function normalizeHomeContent(input: Partial<WebsiteHome> | null | undefined): WebsiteHome {
  return {
    heroTitle: input?.heroTitle || defaultHomeContent.heroTitle,
    heroSubtitle: input?.heroSubtitle || defaultHomeContent.heroSubtitle,
    services: Array.isArray(input?.services) && input.services.length
      ? input.services
      : defaultHomeContent.services,
    references: Array.isArray(input?.references) ? input.references : defaultHomeContent.references,
    process: {
      tag: input?.process?.tag || defaultHomeContent.process.tag,
      title: input?.process?.title || defaultHomeContent.process.title,
      docs: input?.process?.docs || defaultHomeContent.process.docs,
      steps: Array.isArray(input?.process?.steps) && input.process.steps.length
        ? input.process.steps
        : defaultHomeContent.process.steps,
    },
    testimonials: {
      tag: input?.testimonials?.tag || defaultHomeContent.testimonials.tag,
      title: input?.testimonials?.title || defaultHomeContent.testimonials.title,
      items: Array.isArray(input?.testimonials?.items) && input.testimonials.items.length
        ? input.testimonials.items
        : defaultHomeContent.testimonials.items,
    },
    techStack: {
      tag: input?.techStack?.tag || defaultHomeContent.techStack.tag,
      title: input?.techStack?.title || defaultHomeContent.techStack.title,
      moreText: input?.techStack?.moreText || defaultHomeContent.techStack.moreText,
      items: Array.isArray(input?.techStack?.items) && input.techStack.items.length
        ? input.techStack.items
        : defaultHomeContent.techStack.items,
    },
    aboutSection: {
      tag: input?.aboutSection?.tag || defaultHomeContent.aboutSection.tag,
      title: input?.aboutSection?.title || defaultHomeContent.aboutSection.title,
      description: input?.aboutSection?.description || defaultHomeContent.aboutSection.description,
      stats: Array.isArray(input?.aboutSection?.stats) && input.aboutSection.stats.length
        ? input.aboutSection.stats
        : defaultHomeContent.aboutSection.stats,
      values: Array.isArray(input?.aboutSection?.values) && input.aboutSection.values.length
        ? input.aboutSection.values
        : defaultHomeContent.aboutSection.values,
    },
  };
}

type AdminPublicResponse<T> = {
  data?: T;
  error?: string;
};

type AdminProjectDto = {
  id: string;
  name: string;
  nameI18n?: LocalizedText;
  slug: string;
  shortDescription: string;
  shortDescriptionI18n?: LocalizedText;
  description: string;
  descriptionI18n?: LocalizedText;
  coverImage: string | null;
  gallery: string[];
  technologies: string[];
  category: string;
  categoryI18n?: LocalizedText;
  metricsI18n?: {
    tr: string[];
    en: string[];
  };
  projectUrl: string | null;
  githubUrl: string | null;
  caseStudyUrl: string | null;
  demoUrl: string | null;
  order: number;
  featured: boolean;
  publishedAt: string | null;
};

type AdminBlogDto = {
  id: string;
  title: string;
  titleI18n?: LocalizedText;
  slug: string;
  excerpt: string;
  excerptI18n?: LocalizedText;
  content: string;
  contentI18n?: LocalizedText;
  coverImage: string | null;
  category: string;
  categoryI18n?: LocalizedText;
  tags: string[];
  authorName: string;
  seoTitle: string | null;
  seoTitleI18n?: LocalizedText;
  seoDescription: string | null;
  seoDescriptionI18n?: LocalizedText;
  readingTimeI18n?: LocalizedText;
  ogImage: string | null;
  publishedAt: string | null;
  createdAt: string;
};

function mapAdminProject(dto: AdminProjectDto): WebsiteProject {
  return {
    id: dto.id,
    name: dto.name,
    name_i18n: dto.nameI18n,
    slug: dto.slug,
    short_description: dto.shortDescription,
    short_description_i18n: dto.shortDescriptionI18n,
    description: dto.description,
    description_i18n: dto.descriptionI18n,
    cover_image: dto.coverImage,
    gallery: dto.gallery,
    technologies: dto.technologies,
    category: dto.category,
    category_i18n: dto.categoryI18n,
    metrics_i18n: dto.metricsI18n,
    project_url: dto.projectUrl,
    github_url: dto.githubUrl,
    case_study_url: dto.caseStudyUrl,
    demo_url: dto.demoUrl,
    order: dto.order,
    featured: dto.featured,
    published_at: dto.publishedAt,
  };
}

function mapAdminBlog(dto: AdminBlogDto): WebsiteBlogPost {
  return {
    id: dto.id,
    title: dto.title,
    title_i18n: dto.titleI18n,
    slug: dto.slug,
    excerpt: dto.excerpt,
    excerpt_i18n: dto.excerptI18n,
    content: dto.content,
    content_i18n: dto.contentI18n,
    cover_image: dto.coverImage,
    category: dto.category,
    category_i18n: dto.categoryI18n,
    tags: dto.tags,
    author_name: dto.authorName,
    seo_title: dto.seoTitle,
    seo_title_i18n: dto.seoTitleI18n,
    seo_description: dto.seoDescription,
    seo_description_i18n: dto.seoDescriptionI18n,
    reading_time_i18n: dto.readingTimeI18n,
    og_image: dto.ogImage,
    published_at: dto.publishedAt,
    created_at: dto.createdAt,
  };
}

async function fetchFromAdminContentApi<T>(key: string): Promise<T | null> {
  if (!adminContentApiBaseUrl) {
    return null;
  }

  try {
    const response = await fetch(`${adminContentApiBaseUrl}/api/public/content/${key}`);
    if (!response.ok) return null;
    const body = (await response.json()) as AdminPublicResponse<T>;
    if (body.error) return null;
    return (body.data ?? null) as T | null;
  } catch {
    return null;
  }
}

async function queryOrThrow<T>(promise: Promise<{ data: T; error: { message: string } | null }>) {
  const result = await promise;
  if (result.error) {
    throw new Error(result.error.message);
  }
  return result.data;
}

export async function fetchPublishedProjects() {
  const adminData = await fetchFromAdminContentApi<AdminProjectDto[]>('projects');
  if (Array.isArray(adminData)) {
    return adminData.map(mapAdminProject);
  }

  const supabase = getSupabase();
  if (!supabase) {
    return [];
  }

  const data = await queryOrThrow(
    supabase
      .from('projects')
      .select('id, name, slug, short_description, description, cover_image, gallery, technologies, category, order, featured, status, published_at, name_i18n, short_description_i18n, description_i18n, category_i18n, metrics_i18n, project_url, github_url, case_study_url, demo_url')
      .eq('status', 'PUBLISHED')
      .order('featured', { ascending: false })
      .order('order', { ascending: true }),
  );

  return (data ?? []) as WebsiteProject[];
}

export async function fetchProjectBySlugOrId(idOrSlug: string) {
  const adminData = await fetchFromAdminContentApi<AdminProjectDto[]>('projects');
  if (Array.isArray(adminData)) {
    const bySlug = adminData.find((item) => item.slug === idOrSlug);
    if (bySlug) return mapAdminProject(bySlug);

    const byId = adminData.find((item) => item.id === idOrSlug);
    return byId ? mapAdminProject(byId) : null;
  }

  const supabase = getSupabase();
  if (!supabase) {
    return null;
  }

  const bySlug = await queryOrThrow(
    supabase
      .from('projects')
      .select('id, name, slug, short_description, description, cover_image, gallery, technologies, category, order, featured, status, published_at, name_i18n, short_description_i18n, description_i18n, category_i18n, metrics_i18n, project_url, github_url, case_study_url, demo_url')
      .eq('status', 'PUBLISHED')
      .eq('slug', idOrSlug)
      .maybeSingle(),
  );

  if (bySlug) return bySlug as WebsiteProject;

  const byId = await queryOrThrow(
    supabase
      .from('projects')
      .select('id, name, slug, short_description, description, cover_image, gallery, technologies, category, order, featured, status, published_at, name_i18n, short_description_i18n, description_i18n, category_i18n, metrics_i18n, project_url, github_url, case_study_url, demo_url')
      .eq('status', 'PUBLISHED')
      .eq('id', idOrSlug)
      .maybeSingle(),
  );

  return (byId as WebsiteProject | null) ?? null;
}

export function getProjectCoverImage(project: WebsiteProject) {
  const fallback = getProjectFallbackMedia(project.slug);
  return project.cover_image || fallback?.cover || null;
}

export function getProjectGalleryImages(project: WebsiteProject) {
  const cover = getProjectCoverImage(project);
  const fromGallery = Array.isArray(project.gallery) ? project.gallery : [];
  const fallback = getProjectFallbackMedia(project.slug);
  const fromFallback = fallback?.gallery ?? [];

  return normalizeImageList(cover, ...fromGallery, ...fromFallback);
}

export function getProjectTechPills(project: WebsiteProject, locale: "tr" | "en") {
  const category = getLocalizedText(project.category_i18n, locale, project.category);
  const categoryKey = category.trim().toLocaleLowerCase(locale === "tr" ? "tr-TR" : "en-US");

  const unique = new Set<string>();
  const pills: string[] = [];

  for (const item of project.technologies ?? []) {
    const normalized = item.trim();
    if (!normalized) continue;
    const key = normalized.toLocaleLowerCase(locale === "tr" ? "tr-TR" : "en-US");
    if (key === categoryKey || unique.has(key)) continue;
    unique.add(key);
    pills.push(normalized);
  }

  return pills;
}

export async function fetchPublishedBlogPosts() {
  const staticPosts = getStaticBlogPosts();
  const supabase = getSupabase();
  if (!supabase) {
    return staticPosts;
  }

  let remote: WebsiteBlogPost[] = [];

  try {
    const data = await queryOrThrow(
      supabase
        .from('blog_posts')
        .select('*')
        .eq('status', 'PUBLISHED')
        .order('published_at', { ascending: false })
        .order('created_at', { ascending: false }),
    );
    remote = (data ?? []) as WebsiteBlogPost[];
  } catch {
    return staticPosts;
  }

  const bySlug = new Map<string, WebsiteBlogPost>();
  for (const post of staticPosts) bySlug.set(post.slug, post);
  for (const post of remote) {
    bySlug.set(post.slug, mergeBlogPostWithStaticFallback(post, bySlug.get(post.slug)));
  }

  return [...bySlug.values()].sort((a, b) => {
    const aTime = new Date(a.published_at || a.created_at).getTime();
    const bTime = new Date(b.published_at || b.created_at).getTime();
    return bTime - aTime;
  });
}

export async function fetchBlogPostBySlug(slug: string) {
  const canonicalSlug = getBlogCanonicalSlug(slug);
  const supabase = getSupabase();
  if (supabase) {
    try {
      const data = await queryOrThrow(
        supabase
          .from('blog_posts')
          .select('*')
          .eq('status', 'PUBLISHED')
          .eq('slug', canonicalSlug)
          .maybeSingle(),
      );

      if (data) {
        const staticFallback = getStaticBlogPostBySlug(canonicalSlug);
        return mergeBlogPostWithStaticFallback(data as WebsiteBlogPost, staticFallback);
      }
    } catch {
      // fall through to static content
    }
  }

  return getStaticBlogPostBySlug(canonicalSlug);
}

export async function fetchSettingsContent() {
  const adminData = await fetchFromAdminContentApi<WebsiteSettings>('settings');
  if (adminData) {
    return adminData;
  }

  const supabase = getSupabase();
  if (!supabase) {
    return null;
  }

  const data = await queryOrThrow(
    supabase
      .from('content_singletons')
      .select('payload')
      .eq('key', 'settings')
      .maybeSingle(),
  );

  return (data?.payload as WebsiteSettings | null) ?? null;
}

export async function fetchAboutContent() {
  const adminData = await fetchFromAdminContentApi<WebsiteAbout>('about');
  if (adminData) {
    return adminData;
  }

  const supabase = getSupabase();
  if (!supabase) {
    return null;
  }

  const data = await queryOrThrow(
    supabase
      .from('content_singletons')
      .select('payload')
      .eq('key', 'about')
      .maybeSingle(),
  );

  return (data?.payload as WebsiteAbout | null) ?? null;
}

export async function fetchContactContent() {
  const adminData = await fetchFromAdminContentApi<WebsiteContact>('contact');
  if (adminData) {
    return adminData;
  }

  const supabase = getSupabase();
  if (!supabase) {
    return null;
  }

  const data = await queryOrThrow(
    supabase
      .from("content_singletons")
      .select("payload")
      .eq("key", "contact")
      .maybeSingle(),
  );

  return (data?.payload as WebsiteContact | null) ?? null;
}

export async function fetchHomeContent() {
  const adminData = await fetchFromAdminContentApi<WebsiteHome>('home');
  if (adminData) {
    return normalizeHomeContent(adminData);
  }

  const supabase = getSupabase();
  if (!supabase) {
    return normalizeHomeContent(null);
  }

  const data = await queryOrThrow(
    supabase
      .from('content_singletons')
      .select('payload')
      .eq('key', 'home')
      .maybeSingle(),
  );

  return normalizeHomeContent((data?.payload as Partial<WebsiteHome> | null) ?? null);
}
